import { LitElement, html, css } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { Upload, Trash2, X, Image as ImageIcon, Pencil, Camera, ChevronDown, FolderOpen } from 'lucide';
import { lucideIcon } from '../icon.js';
import { api } from '../api.js';
import { confirmDialog } from '../utils/confirm-dialog.js';
import { formatDateTime } from '../utils/datetime.js';
import './camera-venster.js';

/**
 * boring-photos — de Foto's-tab van een boring.
 *
 * Eén platte verzameling, bewust zonder categorieën: een veldwerker fotografeert wat hij
 * ziet, en hem eerst laten kiezen tussen 'situatie', 'lagen' en 'bijzonderheden' kost tijd op
 * het moment dat hij die niet heeft. Wil je een foto tóch ergens aan hangen, dan kan dat bij
 * het uploaden — optioneel, en alleen met de onderdelen die deze boring werkelijk heeft.
 *
 * De afbeeldingen staan achter auth, dus een gewone <img src> werkt niet: de browser stuurt
 * daar geen token mee. Ze worden als blob opgehaald en als object-URL getoond, met een cache
 * per foto zodat opnieuw renderen geen nieuw verzoek doet.
 */
export class BoringPhotos extends LitElement {
  static properties = {
    boringId: { type: String },
    boring: { type: Object },      // voor de keuzelijst: welke onderdelen heeft deze boring?
    photos: { type: Array },
    readonly: { type: Boolean },
    _busy: { state: true },
    _error: { state: true },
    _urls: { state: true },        // fotoId → object-URL
    _form: { state: true },        // uploadvenster; null = dicht
    _lightbox: { state: true },    // foto op volle grootte; null = dicht
    _editing: { state: true },     // foto waarvan de gegevens worden bijgewerkt
    _menu: { state: true },        // keuzemenu van 'Foto toevoegen' open
    _camera: { state: true },      // cameravenster open
  };

  constructor() {
    super();
    this.photos = [];
    this._urls = {};
    this._busy = false;
    this._error = null;
    this._form = null;
    this._lightbox = null;
    this._editing = null;
    this._menu = false;
    this._camera = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this._onEsc = (e) => {
      if (e.key !== 'Escape') return;
      if (this._lightbox) this._lightbox = null;
      else if (this._form) this._form = null;
    };
    document.addEventListener('keydown', this._onEsc);
    // Klik buiten het keuzemenu → dicht.
    this._onKlikBuiten = (e) => { if (this._menu && !e.composedPath().includes(this.renderRoot.querySelector('.toevoegen'))) this._menu = false; };
    document.addEventListener('click', this._onKlikBuiten);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onEsc);
    document.removeEventListener('click', this._onKlikBuiten);
    // Object-URL's vrijgeven; anders blijft het geheugen vasthouden aan elke bekeken foto.
    for (const u of Object.values(this._urls)) URL.revokeObjectURL(u);
    super.disconnectedCallback();
  }

  updated(changed) {
    if (changed.has('photos')) this._loadImages();
  }

  /** Haal de afbeeldingen op die nog niet in de cache zitten. */
  async _loadImages() {
    for (const p of this.photos || []) {
      if (this._urls[p.id]) continue;
      try {
        const blob = await api.fetchPhotoBlob(p.id);
        this._urls = { ...this._urls, [p.id]: URL.createObjectURL(blob) };
      } catch (_) { /* één mislukte foto mag de rest niet blokkeren */ }
    }
  }

  /**
   * Waar kan een foto aan gekoppeld worden? Alleen wat deze boring werkelijk heeft -- een
   * keuze 'Peilbuis' aanbieden bij een boring zonder peilbuis levert niets op.
   */
  _targets() {
    const b = this.boring || {};
    const out = [];
    const layers = (b.layers || []);
    const monsters = (b.monsters || []);
    const peilbuizen = (b.peilbuizen || []);
    const watermonsters = (b.watermonsters || []);
    const fillings = (b.afwerking?.fillings || []);

    if (layers.length) {
      out.push({
        type: 'layer', label: 'Laag',
        options: [...layers]
          .sort((x, y) => (x.upperDepth ?? 0) - (y.upperDepth ?? 0))
          .map((l) => ({ id: l.id, label: `${l.upperDepth ?? '?'}–${l.lowerDepth ?? '?'} cm` })),
      });
    }
    if (monsters.length) {
      out.push({
        type: 'monster', label: 'Grondmonster',
        options: monsters.map((m) => ({ id: m.id, label: m.name || m.identificatie || 'Monster' })),
      });
    }
    if (peilbuizen.length) {
      out.push({
        type: 'peilbuis', label: 'Peilbuis',
        options: peilbuizen.map((p) => ({ id: p.id, label: p.name || 'Peilbuis' })),
      });
    }
    if (watermonsters.length) {
      out.push({
        type: 'watermonster', label: 'Watermonster',
        options: watermonsters.map((w, i) => ({ id: w.id || String(i), label: w.name || `Watermonster ${i + 1}` })),
      });
    }
    if (fillings.length || b.afwerking) {
      out.push({
        type: 'afwerking', label: 'Afwerking',
        options: fillings.map((f, i) => ({ id: String(i), label: `${f.van ?? '?'}–${f.tot ?? '?'} cm` })),
      });
    }
    return out;
  }

  /** 'Laag · 0–40 cm' — wat er onder de foto komt te staan. */
  _labelFor(p) {
    if (!p.targetType) return null;
    const group = this._targets().find((t) => t.type === p.targetType);
    const main = group?.label || p.targetType;
    return p.targetLabel ? `${main} · ${p.targetLabel}` : main;
  }

  _openForm(file) {
    this._error = null;
    this._form = { file, description: '', targetType: '', targetId: '' };
  }

  _onPick(e) {
    const files = [...(e.target.files || [])];
    e.target.value = '';   // zelfde bestand direct opnieuw kunnen kiezen
    if (!files.length) return;
    // Eén bestand → het venster met omschrijving. Meerdere → direct uploaden; wie er tien
    // tegelijk kiest wil niet tien keer een formulier invullen.
    if (files.length === 1) this._openForm(files[0]);
    else this._uploadMany(files);
  }

  async _uploadMany(files) {
    this._busy = true; this._error = null;
    try {
      for (const f of files) await api.uploadBoringPhoto(this.boringId, f);
      this._notify();
    } catch (e) {
      this._error = e?.message || 'Uploaden mislukt.';
    } finally { this._busy = false; }
  }

  async _submitForm() {
    const f = this._form;
    if (!f) return;
    this._busy = true; this._error = null;
    try {
      const group = this._targets().find((t) => t.type === f.targetType);
      const option = group?.options.find((o) => String(o.id) === String(f.targetId));
      await api.uploadBoringPhoto(this.boringId, f.file, {
        description: f.description,
        targetType: f.targetType || null,
        targetId: f.targetId || null,
        // Het label zoals het NU heet meesturen: verandert een laag later van diepte, dan
        // hoort bij de foto nog steeds te staan wat er op dat moment is vastgelegd.
        targetLabel: option?.label || null,
      });
      this._form = null;
      this._notify();
    } catch (e) {
      this._error = e?.message || 'Uploaden mislukt.';
    } finally { this._busy = false; }
  }

  async _saveEdit() {
    const p = this._editing;
    if (!p) return;
    this._busy = true;
    try {
      const group = this._targets().find((t) => t.type === p.targetType);
      const option = group?.options.find((o) => String(o.id) === String(p.targetId));
      await api.updateBoringPhoto(p.id, {
        description: p.description,
        targetType: p.targetType || null,
        targetId: p.targetId || null,
        targetLabel: option?.label || null,
      });
      this._editing = null;
      this._notify();
    } catch (e) {
      this._error = e?.message || 'Opslaan mislukt.';
    } finally { this._busy = false; }
  }

  async _delete(p) {
    if (!await confirmDialog({
      title: `"${p.description || p.filename || 'Deze foto'}" verwijderen?`,
      confirmLabel: 'Verwijderen', danger: true,
    })) return;
    this._busy = true;
    try {
      await api.deleteBoringPhoto(p.id);
      const next = { ...this._urls };
      if (next[p.id]) { URL.revokeObjectURL(next[p.id]); delete next[p.id]; }
      this._urls = next;
      this._notify();
    } catch (e) {
      this._error = e?.message || 'Verwijderen mislukt.';
    } finally { this._busy = false; }
  }

  /** De ouder herlaadt de boring; die is eigenaar van de lijst. */
  _notify() {
    this.dispatchEvent(new CustomEvent('photos-changed', { bubbles: true, composed: true }));
  }

  _onDrop(e) {
    e.preventDefault();
    this._dragging = false;
    if (this.readonly) return;
    const files = [...(e.dataTransfer?.files || [])].filter((f) => f.type.startsWith('image/'));
    if (files.length === 1) this._openForm(files[0]);
    else if (files.length) this._uploadMany(files);
  }

  render() {
    const photos = this.photos || [];
    return html`
      <div class="wrap" @dragover=${(e) => { e.preventDefault(); }} @drop=${this._onDrop}>
        <div class="head">
          <span class="count">
            ${photos.length} foto${photos.length === 1 ? '' : "'s"}${photos.length ? ' · op volgorde van tijd' : ''}
          </span>
          ${this.readonly ? '' : html`
            <!-- Kiezen: een bestand van de computer, of direct een foto met de camera. -->
            <div class="toevoegen">
              <button class="btn" ?disabled=${this._busy} @click=${() => { this._menu = !this._menu; }}
                aria-haspopup="menu" aria-expanded=${this._menu ? 'true' : 'false'}>
                ${unsafeSVG(lucideIcon(Upload, 14))} Foto toevoegen ${unsafeSVG(lucideIcon(ChevronDown, 14))}
              </button>
              ${this._menu ? html`
                <div class="menu" role="menu">
                  <button role="menuitem" @click=${() => { this._menu = false; this.renderRoot.querySelector('#pick').click(); }}>
                    ${unsafeSVG(lucideIcon(FolderOpen, 15))} Bestand
                  </button>
                  <button role="menuitem" @click=${() => { this._menu = false; this._camera = true; }}>
                    ${unsafeSVG(lucideIcon(Camera, 15))} Camera
                  </button>
                </div>` : ''}
            </div>`}
          <input id="pick" type="file" accept="image/*" multiple hidden @change=${this._onPick}>
        </div>

        ${this._error ? html`<div class="err">${this._error}</div>` : ''}

        <div class="grid">
          ${photos.map((p) => this._renderCard(p))}
          ${this.readonly ? '' : html`
            <button class="drop" ?disabled=${this._busy}
              @click=${() => this.renderRoot.querySelector('#pick').click()}>
              ${unsafeSVG(lucideIcon(Upload, 20))}
              <span>${this._busy ? 'Bezig…' : 'sleep of klik'}</span>
            </button>`}
        </div>

        ${this._form ? this._renderForm() : ''}
        <camera-venster .open=${this._camera} modus="foto" titel="Foto maken"
          @sluit=${() => { this._camera = false; }}
          @foto=${(e) => { this._camera = false; this._openForm(e.detail.file); }}></camera-venster>
        ${this._lightbox ? this._renderLightbox() : ''}
        ${this._editing ? this._renderEdit() : ''}
      </div>`;
  }

  _renderCard(p) {
    const url = this._urls[p.id];
    const label = this._labelFor(p);
    return html`
      <figure class="card">
        <button class="thumb" @click=${() => { this._lightbox = p; }} title="Vergroten">
          ${url
            ? html`<img src=${url} alt=${p.description || ''}>`
            : html`<span class="ph">${unsafeSVG(lucideIcon(ImageIcon, 22))}</span>`}
          ${label ? html`<span class="tag">${label}</span>` : ''}
        </button>
        <figcaption>
          <span class="desc ${p.description ? '' : 'none'}">${p.description || 'geen omschrijving'}</span>
          <span class="meta">${formatDateTime(p.createdAt, { day: '2-digit', month: '2-digit', year: undefined })}</span>
        </figcaption>
        ${this.readonly ? '' : html`
          <div class="acts">
            <button title="Gegevens bewerken" @click=${() => { this._editing = { ...p, targetType: p.targetType || '', targetId: p.targetId || '' }; }}>
              ${unsafeSVG(lucideIcon(Pencil, 13))}
            </button>
            <button class="del" title="Verwijderen" @click=${() => this._delete(p)}>
              ${unsafeSVG(lucideIcon(Trash2, 13))}
            </button>
          </div>`}
      </figure>`;
  }

  /** Keuze van onderdeel + specifieke laag/monster. Gedeeld door uploaden en bewerken. */
  _renderTargetPicker(state, onChange) {
    const groups = this._targets();
    if (!groups.length) return '';
    const group = groups.find((t) => t.type === state.targetType);
    return html`
      <label class="fld">
        <span>Hoort bij <em>optioneel</em></span>
        <select .value=${state.targetType || ''}
          @change=${(e) => onChange({ targetType: e.target.value, targetId: '' })}>
          <option value="">Niet gekoppeld</option>
          ${groups.map((t) => html`<option value=${t.type} ?selected=${state.targetType === t.type}>${t.label}</option>`)}
        </select>
      </label>
      ${group && group.options.length > 1 ? html`
        <label class="fld">
          <span>Welke ${group.label.toLowerCase()}? <em>optioneel</em></span>
          <select .value=${state.targetId || ''} @change=${(e) => onChange({ targetId: e.target.value })}>
            <option value="">Hele ${group.label.toLowerCase()}</option>
            ${group.options.map((o) => html`<option value=${o.id} ?selected=${String(state.targetId) === String(o.id)}>${o.label}</option>`)}
          </select>
        </label>` : ''}`;
  }

  _renderForm() {
    const f = this._form;
    return html`
      <div class="back" @click=${() => { if (!this._busy) this._form = null; }}>
        <div class="modal" @click=${(e) => e.stopPropagation()}>
          <div class="mhead">
            <h3>Foto toevoegen</h3>
            <button class="x" @click=${() => { this._form = null; }}>&times;</button>
          </div>
          <div class="mbody">
            <div class="fname">${f.file.name}</div>
            ${this._renderTargetPicker(f, (patch) => { this._form = { ...this._form, ...patch }; })}
            <label class="fld">
              <span>Omschrijving <em>optioneel</em></span>
              <textarea rows="4" placeholder="Bijvoorbeeld: plaatmateriaal, asbestverdacht"
                .value=${f.description}
                @input=${(e) => { this._form = { ...this._form, description: e.target.value }; }}></textarea>
            </label>
          </div>
          <div class="mfoot">
            <button class="btn ghost" @click=${() => { this._form = null; }}>Annuleren</button>
            <button class="btn" ?disabled=${this._busy} @click=${() => this._submitForm()}>
              ${this._busy ? 'Uploaden…' : 'Toevoegen'}
            </button>
          </div>
        </div>
      </div>`;
  }

  _renderEdit() {
    const p = this._editing;
    return html`
      <div class="back" @click=${() => { if (!this._busy) this._editing = null; }}>
        <div class="modal" @click=${(e) => e.stopPropagation()}>
          <div class="mhead">
            <h3>Foto bewerken</h3>
            <button class="x" @click=${() => { this._editing = null; }}>&times;</button>
          </div>
          <div class="mbody">
            ${this._renderTargetPicker(p, (patch) => { this._editing = { ...this._editing, ...patch }; })}
            <label class="fld">
              <span>Omschrijving</span>
              <textarea rows="4" .value=${p.description || ''}
                @input=${(e) => { this._editing = { ...this._editing, description: e.target.value }; }}></textarea>
            </label>
          </div>
          <div class="mfoot">
            <button class="btn ghost" @click=${() => { this._editing = null; }}>Annuleren</button>
            <button class="btn" ?disabled=${this._busy} @click=${() => this._saveEdit()}>Opslaan</button>
          </div>
        </div>
      </div>`;
  }

  _renderLightbox() {
    const p = this._lightbox;
    const url = this._urls[p.id];
    const label = this._labelFor(p);
    return html`
      <div class="back dark" @click=${() => { this._lightbox = null; }}>
        <button class="close" @click=${() => { this._lightbox = null; }} title="Sluiten">
          ${unsafeSVG(lucideIcon(X, 20))}
        </button>
        <figure class="big" @click=${(e) => e.stopPropagation()}>
          ${url ? html`<img src=${url} alt=${p.description || ''}>` : html`<div class="loading">Laden…</div>`}
          <figcaption>
            <span class="d">${p.description || 'geen omschrijving'}</span>
            <span class="m">
              ${label ? html`${label} · ` : ''}${formatDateTime(p.createdAt)}${p.uploadedByEmail ? html` · ${p.uploadedByEmail}` : ''}
            </span>
          </figcaption>
        </figure>
      </div>`;
  }

  static styles = css`
    :host { display: block; }
    .wrap { padding: 12px 14px 16px; }

    .head { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
    .count { font-size: .8rem; color: var(--mv-text-sub); }
    .btn { margin-left: auto; display: inline-flex; align-items: center; gap: 6px; padding: 7px 13px;
      border: none; border-radius: 8px; background: var(--mv-primary); color: #fff; font: inherit;
      font-size: .8rem; font-weight: 700; cursor: pointer; }
    .btn:disabled { opacity: .55; cursor: default; }
    .toevoegen { margin-left: auto; position: relative; }
    .toevoegen .btn { margin-left: 0; }
    .menu {
      position: absolute; right: 0; top: calc(100% + 6px); z-index: 20; min-width: 170px;
      background: var(--mv-surface, #fff); border: 1px solid var(--mv-border); border-radius: 8px;
      box-shadow: 0 8px 24px rgba(0,0,0,0.12); padding: 4px; display: flex; flex-direction: column;
    }
    .menu button {
      display: flex; align-items: center; gap: 8px; padding: 8px 10px; border: none; background: none;
      border-radius: 6px; font: inherit; font-size: 0.88rem; font-weight: 600; color: var(--mv-text); cursor: pointer; text-align: left;
    }
    .menu button:hover { background: var(--mv-surface-alt, #f3f4f6); }
    .btn.ghost { background: none; color: var(--mv-text-sub); border: 1px solid var(--mv-border); }
    .err { margin-bottom: 10px; padding: 8px 11px; border-radius: 7px; font-size: .82rem;
      color: var(--mv-danger, #B91C1C); background: var(--mv-danger-bg, #FEE2E2); }

    .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 14px; }

    .card { margin: 0; position: relative; }
    .thumb { position: relative; display: block; width: 100%; aspect-ratio: 4 / 3; padding: 0;
      border: 1px solid var(--mv-border); border-radius: 10px; overflow: hidden; cursor: zoom-in;
      background: var(--mv-surface-alt); }
    .thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .ph { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
      color: var(--mv-text-muted); }
    /* Het label ligt op de foto: buiten de kaart zou het de rij ongelijk hoog maken. */
    .tag { position: absolute; left: 8px; bottom: 8px; max-width: calc(100% - 16px);
      padding: 3px 8px; border-radius: 6px; background: rgba(17, 20, 26, .78); color: #fff;
      font-size: .68rem; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

    figcaption { display: flex; flex-direction: column; gap: 1px; margin-top: 7px; }
    .desc { font-size: .84rem; font-weight: 600; color: var(--mv-text); line-height: 1.3; }
    .desc.none { font-weight: 500; color: var(--mv-text-muted); font-style: italic; }
    .meta { font-size: .74rem; color: var(--mv-text-muted); }

    /* Acties pas bij hover: ze horen niet op elke foto permanent in beeld te staan. */
    .acts { position: absolute; top: 7px; right: 7px; display: flex; gap: 4px; opacity: 0;
      transition: opacity .12s ease; }
    .card:hover .acts { opacity: 1; }
    .acts button { display: inline-flex; padding: 5px; border: none; border-radius: 6px; cursor: pointer;
      background: rgba(17, 20, 26, .72); color: #fff; }
    .acts .del:hover { background: var(--mv-danger, #EF4444); }

    .drop { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 7px;
      aspect-ratio: 4 / 3; border: 1.5px dashed var(--mv-border-dashed, var(--mv-border)); border-radius: 10px;
      background: none; cursor: pointer; color: var(--mv-text-muted); font: inherit; font-size: .8rem; }
    .drop:hover { border-color: var(--mv-primary); color: var(--mv-primary); }

    .back { position: fixed; inset: 0; z-index: var(--mv-z-modal, 1200); background: rgba(15, 23, 42, .45);
      display: flex; align-items: center; justify-content: center; padding: 5vh 1rem; }
    .back.dark { background: rgba(8, 10, 14, .88); }

    .modal { width: min(460px, 100%); background: var(--mv-surface); border-radius: .8rem;
      box-shadow: 0 24px 60px rgba(0, 0, 0, .25); display: flex; flex-direction: column; }
    .mhead { display: flex; align-items: center; padding: 1rem 1.25rem .75rem; border-bottom: 1px solid var(--mv-border); }
    .mhead h3 { margin: 0; font-size: 1rem; font-weight: 700; color: var(--mv-text); }
    .x { margin-left: auto; background: none; border: none; cursor: pointer; font-size: 1.4rem; line-height: 1;
      color: var(--mv-text-sub); }
    .mbody { padding: 1rem 1.25rem; display: flex; flex-direction: column; gap: 13px; }
    .fname { font-size: .78rem; color: var(--mv-text-muted); word-break: break-all; }
    .fld { display: flex; flex-direction: column; gap: 5px; }
    .fld span { font-size: .78rem; font-weight: 600; color: var(--mv-text-sub); }
    .fld em { font-style: normal; font-weight: 500; color: var(--mv-text-muted); }
    .fld input, .fld select, .fld textarea { padding: 8px 10px; border: 1px solid var(--mv-border);
      border-radius: 7px; font: inherit; font-size: .86rem; background: var(--mv-surface); color: var(--mv-text); }
    /* Een omschrijving is vaak een halve zin over wat er te zien is; die hoort niet in een
       eenregelig veldje te passen waar je doorheen moet scrollen. */
    .fld textarea { min-height: 84px; resize: vertical; line-height: 1.45; }
    .fld textarea::placeholder { color: var(--mv-text-muted); }
    .fld input:focus, .fld select:focus, .fld textarea:focus { outline: none;
      border-color: var(--mv-primary); box-shadow: 0 0 0 3px var(--mv-primary-soft, rgba(34, 139, 76, .16)); }
    .mfoot { display: flex; justify-content: flex-end; gap: 8px; padding: .75rem 1.25rem 1.1rem; }
    .mfoot .btn { margin-left: 0; }

    .close { position: fixed; top: 18px; right: 22px; display: inline-flex; padding: 8px; border: none;
      border-radius: 8px; background: rgba(255, 255, 255, .12); color: #fff; cursor: pointer; }
    .big { margin: 0; max-width: min(1100px, 92vw); max-height: 88vh; display: flex; flex-direction: column; gap: 10px; }
    .big img { max-width: 100%; max-height: 78vh; object-fit: contain; border-radius: 8px; display: block; margin: 0 auto; }
    .big figcaption { align-items: center; text-align: center; color: #fff; }
    .big .d { font-size: .95rem; font-weight: 600; }
    .big .m { font-size: .78rem; color: rgba(255, 255, 255, .65); }
    .loading { color: rgba(255, 255, 255, .7); font-size: .9rem; padding: 3rem; }
  `;
}

customElements.define('boring-photos', BoringPhotos);
